import { motion } from 'framer-motion';
import { Sparkles, CheckCircle2, ArrowRight } from 'lucide-react';
import { PublicPanelShell } from '../../components/ui/PublicPanelShell';
import PublishButton from '../../components/ui/PublishButton';
import { usePublish } from '../../lib/usePublish';
import VideroMark from '../../components/ui/VideroMark';

interface Props {
  onEnter: () => void;
}

const HIGHLIGHTS = [
  'La IA generó contenido de arranque para cada sección de tu sitio.',
  'Revisa los textos y fotos — algunos son de muestra y necesitan tu toque.',
  'Cuando estés listo, publica y tu sitio estará en línea al instante.',
];

export default function OnboardingCompletedScreen({ onEnter }: Props) {
  const { phase, error, publish } = usePublish();

  return (
    <PublicPanelShell widthClassName="max-w-lg">
      <div className="text-center space-y-8">
        {/* Icono animado */}
        <div className="flex flex-col items-center gap-4">
          <motion.div
            initial={{ scale: 0.4, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 260, damping: 16, delay: 0.1 }}
            className="relative flex h-20 w-20 items-center justify-center rounded-3xl bg-ui-accent text-ui-text-inverse shadow-xl shadow-ui-accent/30"
          >
            <VideroMark className="h-11 w-11" />
            <motion.span
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 400, damping: 14, delay: 0.5 }}
              className="absolute -bottom-2 -right-2 flex h-7 w-7 items-center justify-center rounded-full bg-green-500 shadow-lg"
            >
              <CheckCircle2 className="h-4 w-4 text-white" strokeWidth={2.5} />
            </motion.span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.3 }}
          >
            <div className="flex items-center justify-center gap-1.5 mb-2">
              <Sparkles className="h-4 w-4 text-ui-accent" />
              <span className="text-sm font-medium text-ui-accent">Sitio creado</span>
            </div>
            <h1 className="font-display text-3xl font-semibold text-ui-text leading-tight">
              ¡Tu sitio está listo!
            </h1>
            <p className="mt-2 text-sm text-ui-text-muted max-w-sm mx-auto">
              La IA hizo la mayor parte del trabajo. Solo necesitas revisar algunos
              detalles para que todo quede con tu sello personal.
            </p>
          </motion.div>
        </div>

        {/* Qué sigue */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.5 }}
          className="rounded-2xl border border-ui-border bg-ui-surface p-5 text-left space-y-3"
        >
          <p className="text-sm font-medium text-ui-text">Qué te queda por hacer</p>
          {HIGHLIGHTS.map((text, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -6 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.25, delay: 0.6 + i * 0.1 }}
              className="flex items-start gap-3"
            >
              <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-ui-accent/15 text-ui-accent text-xs font-bold">
                {i + 1}
              </span>
              <p className="text-sm text-ui-text-muted leading-snug">{text}</p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.75 }}
          className="rounded-2xl border border-amber-400/30 bg-amber-400/10 p-5 text-left space-y-3"
        >
          <p className="text-sm font-medium text-ui-text">Siguiente paso recomendado</p>
          <p className="text-sm text-ui-text-muted">
            Publica tu sitio para que quede visible en línea con tu dominio actual.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <PublishButton
              label="Publicar mi sitio"
              onPublish={publish}
              className="inline-flex items-center justify-center rounded-xl bg-amber-400 px-4 py-2.5 text-sm font-semibold text-amber-950 hover:bg-amber-300 disabled:opacity-50 transition-colors"
            />
            {phase === 'publishing' && (
              <p className="text-sm text-ui-text-muted">
                Estamos iniciando la publicación. Podrás seguir editando desde el panel.
              </p>
            )}
            {phase === 'done' && (
              <p className="text-sm text-emerald-400">
                Publicación iniciada correctamente.
              </p>
            )}
          </div>
          {error && <p className="text-sm text-ui-danger">{error}</p>}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, delay: 0.9 }}
        >
          <motion.button
            type="button"
            onClick={onEnter}
            whileTap={{ scale: 0.97 }}
            whileHover={{ opacity: 0.92 }}
            className="inline-flex items-center gap-2 px-6 py-3 bg-ui-accent text-ui-text-inverse text-sm font-semibold rounded-xl shadow-lg shadow-ui-accent/20 transition-opacity"
          >
            Entrar a mi panel <ArrowRight className="h-4 w-4" />
          </motion.button>
          <p className="mt-3 text-sm text-ui-text-subtle">
            En la pantalla de inicio encontrarás una guía con todo lo que te queda.
          </p>
        </motion.div>
      </div>
    </PublicPanelShell>
  );
}
